import { useNavigate, useParams } from 'react-router-dom';
import CustomHistory from '../components/customHistory/CustomHistorySection';
import Footer from '../components/Footer/FooterSection';
import HeaderTwo from '../components/HeaderTwo/HeaderTwoSection';
import '../styles/order.scss';
import '../styles/trackingdetails.scss'; // Import tracking styles for timeline
import { BoxIcon, Hourglass } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNotification } from '../context/NotificationContext';
import { getReturnedOrderDetails } from '../api/requests';
import SkeletonLoader from '../components/SkeletonLoaders/DefaultLoader';
import { ReturnedOrderDetails } from '../interface/interfaces';
import 'animate.css';

type pageProp = {
 orderData: ReturnedOrderDetails | null;
 isLoading: boolean;
 max: number;
};

const ReturnedOrder = () => {
 const navigate = useNavigate();
 const { id } = useParams();
 const { notify } = useNotification();

 const [pageData, setPageData] = useState<pageProp>({
  orderData: null,
  isLoading: false,
  max: 5, // Default max steps for timeline
 });

 const setPageLoader = (value: boolean) => {
  setPageData((prev) => {
   return {
    ...prev,
    isLoading: value,
   };
  });
 };

 useEffect(() => {
  const func = async () => {
   if (!id) {
    notify.error('Invalid link, please reload!');
    return;
   }

   try {
    setPageLoader(true);
    let result = await getReturnedOrderDetails(id);

    if (result) {
     setPageData((prev) => {
      return {
       ...prev,
       orderData: result,
      };
     });
    } else {
     notify.error('Order not found!');
    }
   } catch (error: any) {
    notify.error(error.message);
   } finally {
    setPageLoader(false);
   }
  };

  func();
  // eslint-disable-next-line
 }, []);

 const dateFunc = (rawDate: string): string => {
  const date = new Date(rawDate);

  const opts: Intl.DateTimeFormatOptions = {
   year: 'numeric',
   month: 'long',
   day: 'numeric',
   hour: 'numeric',
   minute: 'numeric',
  };

  const formatted = date.toLocaleDateString('en-US', opts);
  return formatted;
 };

 const handleTrack = () => {
  navigate(`/tracking/${pageData.orderData?.order_id}/${id}`);
 };

 return (
  <>
   <HeaderTwo></HeaderTwo>

   <CustomHistory></CustomHistory>

   <div className="content order">
    {pageData.isLoading && (
     <SkeletonLoader count={4}></SkeletonLoader>
    )}

    {!pageData.isLoading && pageData.orderData && (
     <div className="animate__animated animate__fadeIn animate__fast">
      <div className="new_header_container">
       <div className="pagetitle">
        <div
         className="icon goback"
         onClick={() => navigate(-1)}
        ></div>
        <div>Returned Order Details</div>
       </div>
      </div>

      <div className="orderdet">
       <div className="statusicon yellow">
        <Hourglass
         size={22}
         strokeWidth={1.5}
        ></Hourglass>
       </div>

       <div className="orderinfo">
        <div className="ordertitle">
         Order{' '}
         <span className="cap">{pageData.orderData.order_id}</span>
        </div>
        <div className="ordertxt">
         Placed on {dateFunc(pageData.orderData.createdAt)}
        </div>
        <div className="statustext">{pageData.orderData.status}</div>
       </div>
      </div>

      <div className="title">Product Details</div>

      <div className="orderbox">
       <div className="eachbox">
        <div className="product-info">
         <div className="imageBox">
          {pageData.orderData.product_image.length >= 1 && (
           <img
            src={pageData.orderData.product_image[0]}
            className="image"
            alt="Product"
           ></img>
          )}
          {pageData.orderData.product_image.length <= 0 && (
           <BoxIcon
            size={40}
            strokeWidth={1}
            color="var(--gray-one)"
           ></BoxIcon>
          )}
         </div>

         <div className="textcontent">
          <div className="productname">
           {pageData.orderData.product_name}
          </div>
         </div>
        </div>

        <div className="action_area">
         <button
          className="btn full"
          onClick={handleTrack}
         >
          Track Item
         </button>
        </div>
       </div>
      </div>

      <div
       className="twoGrid"
       style={{ marginTop: '40px', marginBottom: '20px' }}
      >
       <div className="eachgdetail payinfo">
        <div className="gheader">
         <div className="gtitle">Return Information</div>
        </div>
        <div className="gcontent">
         <div className="gdetails">
          <div className="title">Return Reason</div>
          <div className="text">
           {pageData.orderData.return_reason}
          </div>
         </div>
         <div className="gdetails">
          <div className="title">Delivery Payment Status</div>
          <div
           className="text"
           style={{ textTransform: 'capitalize' }}
          >
           {pageData.orderData.delivery_payment_status}
          </div>
         </div>
        </div>
       </div>

       <div className="eachgdetail deliveryinfo">
        <div className="gheader">
         <div className="gtitle">Pickup Details</div>
        </div>
        <div className="gcontent">
         <div className="gdetails">
          <div className="title">Pickup Address</div>
          <div className="text">
           {pageData.orderData.pickup_address}
          </div>
         </div>
         <div className="gdetails">
          <div className="title">Customer Contact</div>
          <div className="text">
           {pageData.orderData.customer_telephone_no}
          </div>
         </div>
        </div>
       </div>
      </div>
     </div>
    )}
   </div>

   <Footer></Footer>
  </>
 );
};

export default ReturnedOrder;
